import type {
  ClientConfig,
  RadarConfig,
  TimeoutConfig,
} from "@/features/client-config/types/client-config";
import { fillMapOriginFromDefaultRadar } from "@/features/client-config/utils/fill-map-origin-from-default-radar";
import { resolveDefaultRadar } from "@/features/client-config/utils/resolve-default-radar";

type LegacyRadarConfig = {
  webSocketBaseUrl: string;
  radarHttpBaseUrl: string;
  timeoutConfig?: TimeoutConfig;
  systemConfig?: RadarConfig["systemConfig"];
  convertedCourseData?: RadarConfig["convertedCourseData"];
  selectedLaunchAreaIds?: RadarConfig["selectedLaunchAreaIds"];
};

type StoredRadarConfig = Omit<
  RadarConfig,
  "enabled" | "source" | "launchAreasReviewed"
> &
  Partial<Pick<RadarConfig, "enabled" | "source" | "launchAreasReviewed">>;

type StoredFeatureFlags = Partial<
  Pick<ClientConfig, "debugConsoleEnabled" | "kioskLockEnabled">
>;

type LegacyStoredClientConfig = Omit<
  ClientConfig,
  "radars" | keyof StoredFeatureFlags
> &
  StoredFeatureFlags & {
    radarConfig?: LegacyRadarConfig;
    radars?: undefined;
  };

type CurrentStoredClientConfig = Omit<
  ClientConfig,
  "radars" | keyof StoredFeatureFlags
> &
  StoredFeatureFlags & {
    radars: StoredRadarConfig[];
  };

type StoredClientConfig = CurrentStoredClientConfig | LegacyStoredClientConfig;

const isLegacyShape = (
  config: StoredClientConfig
): config is LegacyStoredClientConfig => !Array.isArray(config.radars);

const legacyRadarId = (radarConfig: LegacyRadarConfig): string =>
  `legacy-${radarConfig.webSocketBaseUrl}`;

/**
 * The persisted ClientConfig shape is unchanged by the document format
 * (docs/config-consolidation/04-JSON-SCHEMA-DESIGN.md#document-shape-vs-persisted-shape),
 * so a v0 (pre-consolidation) stored config already satisfies ClientConfig
 * except for the single-radar -> multi-radar shape migrated here.
 * This normalizes in memory only — callers must not persist the result
 * unless they are already writing the config for another reason.
 */
export const normalizeStoredConfig = (
  config: StoredClientConfig | null
): ClientConfig | null => {
  if (!config) {
    return null;
  }

  if (!isLegacyShape(config)) {
    return fillMissingDefaultRadarFields({
      ...fillMissingFeatureFlags(config),
      radars: config.radars.map(fillMissingRadarFields),
    });
  }

  const { radarConfig, ...rest } = config;
  const radars: RadarConfig[] = radarConfig
    ? [
        fillMissingRadarFields({
          id: legacyRadarId(radarConfig),
          webSocketBaseUrl: radarConfig.webSocketBaseUrl,
          radarHttpBaseUrl: radarConfig.radarHttpBaseUrl,
          systemConfig: radarConfig.systemConfig,
          convertedCourseData: radarConfig.convertedCourseData,
          selectedLaunchAreaIds: radarConfig.selectedLaunchAreaIds,
        }),
      ]
    : [];

  return fillMissingDefaultRadarFields({
    ...fillMissingFeatureFlags(rest),
    radars,
    timeoutConfig: radarConfig?.timeoutConfig,
  });
};

const fillMissingFeatureFlags = <T extends StoredFeatureFlags>(
  config: T
): T & Required<StoredFeatureFlags> => ({
  ...config,
  debugConsoleEnabled: config.debugConsoleEnabled ?? false,
  kioskLockEnabled: config.kioskLockEnabled ?? false,
});

const fillMissingRadarFields = (radar: StoredRadarConfig): RadarConfig => ({
  ...radar,
  enabled: radar.enabled ?? true,
  source: radar.source ?? "cloud",
  launchAreasReviewed: radar.launchAreasReviewed ?? false,
});

const fillMissingDefaultRadarFields = (config: ClientConfig): ClientConfig => {
  const defaultRadar = resolveDefaultRadar(
    config.radars,
    config.defaultRadarId
  );
  const withDefaultId: ClientConfig = {
    ...config,
    defaultRadarId: defaultRadar?.id,
  };
  if (withDefaultId.golfCourseConfig?.targetPinsConfig?.originPosition) {
    return withDefaultId;
  }
  return fillMapOriginFromDefaultRadar(withDefaultId);
};
